import { isJwtTokenExpired, isString } from '@allurion/utils';

import { fetchTwilioToken } from './TwilioApi';

const TWILIO_KEY_TOKEN = 'tw-conversations-token';

/**
 * Treat a token as spent this long before its `exp`.
 *
 * A cached token is only ever read in order to *reuse* it, and one with
 * seconds left passes a bare expiry check and then dies inside the Twilio
 * client as `Access Token expired` (20104/401, terminal) — the connection is
 * gone and the SDK does not retry a terminal error. `sessionStorage` outlives
 * a login, so the token being reused can be most of an hour old.
 *
 * Two minutes sits just inside the SDK's own `tokenAboutToExpire`, which fires
 * three minutes out: if the client is alive the SDK renews first, and this is
 * what catches the case where no client is alive to renew.
 */
export const TOKEN_EXPIRY_MARGIN_SECONDS = 120;

// The Twilio JWT is a short-lived (default 1h) chat credential. Keep it in
// sessionStorage rather than localStorage so it is scoped to the tab/session
// and is not shared across tabs or left behind for the next user on a shared
// workstation. The auth logout flow additionally calls sessionStorage.clear().
export function clearTwilioToken() {
  sessionStorage.removeItem(TWILIO_KEY_TOKEN);
}

function saveTwilioToken(token: string, identity: string) {
  sessionStorage.setItem(TWILIO_KEY_TOKEN, JSON.stringify({ token, identity }));
}

function getSavedTwilioToken() {
  const savedToken = sessionStorage.getItem(TWILIO_KEY_TOKEN);

  if (!savedToken) {
    return {};
  }

  try {
    const { token, identity } = JSON.parse(savedToken);

    if (
      !isString(token) ||
      !isString(identity) ||
      isJwtTokenExpired(token, TOKEN_EXPIRY_MARGIN_SECONDS)
    ) {
      clearTwilioToken();

      return {};
    }

    return { token, identity };
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return {};
  }
}

export async function getTwilioToken(username: string) {
  const { identity, token: savedToken } = getSavedTwilioToken();

  if (username === identity) {
    return savedToken;
  }

  const token = await fetchTwilioToken(username);

  saveTwilioToken(token, username);

  return token;
}
